import { Collapse, List, Typography, alpha, useTheme } from '@mui/material';
import { Paraphrase } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import TextSuggestion from './text-suggestion';

interface Props {
  paraphrase: Paraphrase;
  active: boolean;
  /** 'default' keeps the legacy card, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';
  /** Hides the option list – used when the panel card offers a single "apply" button */
  hideOptions?: boolean;

  insertAtPosition(word: string, pos: number | number[]): () => void;
}

const ParaphraseContent: React.FC<Props> = ({ paraphrase, active, variant = 'default', hideOptions, insertAtPosition }) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const isPanel = variant === 'panel';
  const firstOption = !_.isEmpty(paraphrase.suggestions) ? paraphrase.suggestions[0] : undefined;

  return (
    <div>
      {isPanel ? (
        <Typography className={classes.panelReplacement}>
          {!_.isNil(paraphrase.replacedSpan) ? <span className={classes.panelOriginalText}>{paraphrase.replacedSpan.content}</span> : null}
          {!_.isNil(paraphrase.replacedSpan) && hideOptions && !_.isNil(firstOption) ? <span className={classes.panelArrow}>{' → '}</span> : null}
          {hideOptions && !_.isNil(firstOption) ? <span className={classes.panelNewText}>{firstOption.content}</span> : null}
        </Typography>
      ) : null}
      <Typography className={isPanel ? classes.panelExplanation : classes.explanation}>{paraphrase.text}</Typography>
      {!isPanel && !_.isNil(paraphrase.replacedSpan) ? (
        <div style={{ textDecoration: 'line-through', color: alpha(theme.palette.error.main, 0.67) }}>
          <Typography className={classes.originalText}>{paraphrase.replacedSpan.content}</Typography>
        </div>
      ) : null}
      {!hideOptions ? (
        <Collapse in={active}>
          <List disablePadding={true}>
            {paraphrase.suggestions.map((suggestion, index) => (
              <TextSuggestion
                key={index}
                suggestion={suggestion}
                position={paraphrase.highlightPosition}
                variant={variant}
                insertAtPosition={insertAtPosition}
              />
            ))}
          </List>
        </Collapse>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  explanation: {
    fontSize: theme.typography.pxToRem(11),
    marginBottom: '8px',
  },
  originalText: {
    fontSize: theme.typography.pxToRem(11),
    marginBottom: '8px',
    color: '#000',
  },
  panelExplanation: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.5,
    color: theme.palette.greys.medium,
    textWrap: 'pretty',
  },
  panelReplacement: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.5,
    marginBottom: '10px',
  },
  panelOriginalText: {
    textDecoration: 'line-through',
    color: theme.palette.greys.medium,
  },
  panelArrow: {
    color: theme.palette.greys.medium,
  },
  panelNewText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
}));

export default ParaphraseContent;
